import { FileText, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { SummarizeConfig } from '@new-signal/shared';
import { cn } from '../lib/utils';

interface SummarizeCardProps {
  config: SummarizeConfig;
  onChange: (config: SummarizeConfig) => void;
  onRemove: () => void;
}

export function SummarizeCard({ config, onChange, onRemove }: SummarizeCardProps) {
  const { t } = useTranslation('feed-builder');

  const setLength = (length: SummarizeConfig['length']) => {
    onChange({ ...config, length });
  };

  return (
    <div className="mb-4 rounded-xl border border-brand-border bg-brand-canvas p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText size={17} strokeWidth={1.75} className="text-accent-green" aria-hidden />
          <strong>{t('summarize.title')}</strong>
        </div>
        <button
          type="button"
          onClick={onRemove}
          title={t('summarize.removeAction')}
          aria-label={t('summarize.removeAction')}
          className="inline-flex size-7 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-brand-muted hover:bg-brand-surface hover:text-brand-text"
        >
          <X size={15} strokeWidth={1.75} aria-hidden />
        </button>
      </div>

      <div className="mb-3">
        <span className="mb-1.5 block text-sm text-brand-subtle">{t('summarize.lengthLabel')}</span>
        <div className="grid grid-cols-2 rounded-lg border border-brand-border-medium bg-white p-0.5">
          {(['short', 'medium'] as const).map((length) => (
            <button
              key={length}
              type="button"
              onClick={() => setLength(length)}
              className={cn(
                'min-h-9 cursor-pointer rounded-md border-none px-3 text-sm font-medium',
                config.length === length
                  ? 'bg-accent-blue text-brand-bg'
                  : 'bg-transparent text-brand-text hover:bg-brand-surface',
              )}
            >
              {t(`summarize.length.${length}`)}
            </button>
          ))}
        </div>
      </div>

      <label className="flex min-h-9 cursor-pointer items-center justify-between gap-3 rounded-lg border border-brand-border-medium bg-white px-3 py-2 text-sm text-brand-text">
        <span>{t('summarize.includeReadTime')}</span>
        <input
          type="checkbox"
          checked={config.include_read_time}
          onChange={(e) => onChange({ ...config, include_read_time: e.target.checked })}
          className="size-4 accent-accent-blue"
        />
      </label>
    </div>
  );
}
